// Every check on the project's runners, on the queue strip (Poulpe-780, from
// Poulpe-776's mockups): one line with a tiny bar per slot, and a Checks tab
// grouped by machine, with each slot's running check, its parts, and the
// line in order. Busy machines open; idle ones fold to one line. The Last
// hour tab, and the `~` estimates from comparable runs, are Poulpe-781's.
import { Link } from 'react-router-dom'
import type { MachineCheck, MachinePart, QueueDetail, QueueMachine } from '../api/types'
import {
  avatar, busy, clock, countText, diskText, estimateTitle, hourRows, leftText, loadOf, machines, nearLimit,
  partLabel, share, sourceText, startText, summary, usualText, waited, waitingSeries, whatText,
} from './machineWords'
import { duration, since } from './detail'
import { short } from './words'

/** The checks in the strip's one line: a bar per slot, how many run, and who waits. */
export function ChecksLine({ detail }: { detail: QueueDetail }) {
  const all = machines(detail)
  const said = summary(detail)
  if (said.running === 0 && said.waiting === 0) return <IdleLine detail={detail} />
  const slots = all.filter(busy).flatMap((machine) => machine.slots.map((slot) => ({ machine, slot })))
  return <span className="checks-line">
    <span className="checks-mini" aria-hidden="true">
      <span className="checks-faint">Checks</span>
      {slots.slice(0, 6).map(({ machine, slot }) => {
        const check = slot.running[0]
        return <span key={`${machine.runner}:${slot.name}`} className="checks-minibar" title={check ? `${machine.name}, ${slot.name} slot: ${whatText(check)}` : `${machine.name}, ${slot.name} slot: free`}>
          {check && miniParts(check).map((piece, index) => <i key={index} className={`is-${piece.state}`} style={{ width: `${piece.width}%` }} />)}
        </span>
      })}
    </span>
    <span>{said.running} running</span>
    {said.waiting > 0 && <span className={`checks-chip is-${said.tone}`}>
      <span className="checks-dot" />{said.waiting} waiting · longest {duration(said.longest)}
    </span>}
  </span>
}

/** Each part's slice of a slot's tiny bar: its share of the parts, filled as far as it has gone. */
function miniParts(check: MachineCheck): { state: MachinePart['state']; width: number }[] {
  const parts = check.parts.length ? check.parts : [{ name: check.part ?? 'check', state: 'running' as const }]
  const each = 100 / parts.length
  return parts.flatMap((part) => {
    if (part.state === 'to_come') return []
    const done = share(part, check)
    return [{ state: part.state, width: each * (done ?? 0.5) }]
  })
}

/** No check runs or waits: main's last result instead. */
function IdleLine({ detail }: { detail: QueueDetail }) {
  const main = detail.main_check
  return <span className="checks-line">
    <span className="checks-faint">Checks idle{main && <> · main <code>{short(main.sha)}</code></>}</span>
    {main && <span className={`checks-chip is-${main.result === 'passed' ? 'ok' : 'danger'}`}>
      <span className="checks-dot" />{main.result}{main.at && ` ${since(main.at, detail.measured_at)} ago`}
    </span>}
  </span>
}

/** The Checks tab: each machine of the project, busy ones open. */
export function ChecksTab({ detail }: { detail: QueueDetail }) {
  const all = machines(detail)
  if (all.length === 0) return <p className="muted">No runner serves this project.</p>
  return <div className="checks-tab">
    {all.map((machine) => busy(machine)
      ? <Machine key={machine.runner} machine={machine} measuredAt={detail.measured_at} />
      : <FoldedMachine key={machine.runner} machine={machine} measuredAt={detail.measured_at} />)}
    <p className="checks-legend">
      <span><span className="checks-swatch is-passed" />part passed</span>
      <span><span className="checks-swatch is-running" />running</span>
      <span><span className="checks-swatch is-failed" />failed</span>
      <span><span className="checks-swatch is-to_come" />to come</span>
      <span className="checks-faint">Queue checks wait ahead of hand checks. Times marked ~ are estimated from comparable runs.</span>
    </p>
  </div>
}

function FoldedMachine({ machine, measuredAt }: { machine: QueueMachine; measuredAt: string }) {
  const status = !machine.connected ? `offline${machine.last_seen_at ? ` ${since(machine.last_seen_at, measuredAt)}` : ''}`
    : machine.reported_at === null ? 'no report of its checks' : null
  return <section className="checks-machine is-folded" aria-label={machine.name}>
    <div className="checks-machine-head">
      <span className="checks-machine-name">{machine.name}</span>
      <span className="checks-faint">idle</span>
      <span className="checks-grow" />
      {status && <span className="checks-faint">{status}</span>}
    </div>
  </section>
}

function Machine({ machine, measuredAt }: { machine: QueueMachine; measuredAt: string }) {
  const load = loadOf(machine)
  const disk = diskText(machine.disk_free_bytes)
  const waiting = machine.slots.flatMap((slot) => slot.waiting.map((check, index) => ({ slot: slot.name, check, next: index === 0 && slot.running.length === 0 || check.state === 'taking' })))
  return <section className="checks-machine" aria-label={machine.name}>
    <div className="checks-machine-head">
      <span className="checks-machine-name">{machine.name}</span>
      <span className="checks-src">runner</span>
      {!machine.connected && <span className="checks-faint">offline</span>}
      <span className="checks-grow" />
      {load && <span className="checks-gauge">CPU{load.share !== null && <span className="checks-gauge-bar"><i className={load.share > 0.85 ? 'is-failed' : load.share > 0.6 ? 'is-warn' : 'is-passed'} style={{ width: `${Math.round(load.share * 100)}%` }} /></span>} {load.text}</span>}
      {disk && <span className="checks-gauge">Disk {disk}</span>}
    </div>
    {machine.slots.map((slot) => <div key={slot.name} className="checks-slot">
      <div className="checks-slot-name">{slot.name}</div>
      <div>
        {slot.running.length === 0 ? <p className="checks-faint checks-free">free</p>
          : slot.running.map((check, index) => <Running key={index} check={check} measuredAt={measuredAt} />)}
      </div>
    </div>)}
    {waiting.length > 0 && <div className="checks-slot">
      <div className="checks-slot-name">Waiting</div>
      <ol className="checks-line-up" aria-label={`Waiting on ${machine.name}, in order`}>
        {waiting.map(({ slot, check, next }, index) => <Waiting key={index} place={index + 1} slot={slot} several={machine.slots.length > 1} check={check} next={next} measuredAt={measuredAt} />)}
      </ol>
    </div>}
  </section>
}

/** Who holds or waits: the agent, the queue or a person, and its issue. */
function Who({ check }: { check: MachineCheck }) {
  const face = avatar(check)
  if (check.anonymous) return <span className="checks-who"><span className="muted">A check outside this workspace</span><span className="checks-src">{check.source === 'queue' ? 'Queue' : check.source}</span></span>
  return <span className="checks-who">
    {face && <span className={`checks-avatar is-${face.tone}`} aria-hidden="true">{face.letter}</span>}
    {check.source === 'person' && <span>A person · Terminal</span>}
    {check.source === 'unknown' && <span className="muted">A check with no record</span>}
    {check.issue && <Link to={`/${encodeURIComponent(check.issue.key)}`} className="checks-key">{check.issue.key}</Link>}
    {check.issue && <span className="checks-title">{check.issue.title}</span>}
    <span className={`checks-src${check.source === 'queue' ? ' is-queue' : ''}`}>{sourceText(check)}</span>
  </span>
}

function Running({ check, measuredAt }: { check: MachineCheck; measuredAt: string }) {
  const count = countText(check)
  const inPart = since(check.part_since, measuredAt)
  const current = check.parts.find((part) => part.state === 'running')
  const left = leftText(check, measuredAt)
  const usual = current && share(current, check) === null ? usualText(check) : null
  return <div className="checks-run">
    <div>
      <Who check={check} />
      <div className="checks-what"><code>{whatText(check)}</code>{check.running_since && ` · running ${since(check.running_since, measuredAt)}`}</div>
    </div>
    <div>
      <Parts check={check} />
      <div className="checks-meta">
        <span>{current ? (count ?? 'no count to read') : count ?? ''}{inPart && ` · ${inPart} in part`}{usual && ` (${usual})`}</span>
        <span>{check.parts.some((part) => part.state === 'failed') ? 'failed'
          : left && check.estimate ? <span className="checks-estimate" title={estimateTitle(check.estimate)}>{left}</span> : ''}</span>
      </div>
    </div>
  </div>
}

/** The check's parts, segmented: passed, running with its count, failed, to come. */
function Parts({ check }: { check: MachineCheck }) {
  const parts = check.parts.length ? check.parts : [{ name: check.part ?? 'check', state: 'running' as const }]
  return <div className="checks-parts" role="list" aria-label="Parts">
    {parts.map((part, index) => {
      const done = share(part, check)
      return <div key={`${part.name}:${index}`} role="listitem" className={`checks-part is-${part.state}`} aria-label={`${part.name}: ${part.state.replace('_', ' ')}`}>
        <div className="checks-bar">
          {part.state === 'running' && done === null ? <i className="is-indeterminate" />
            : done ? <i style={{ width: `${Math.round(done * 100)}%` }} /> : null}
        </div>
        <div className="checks-part-label">{partLabel(part, check)}</div>
      </div>
    })}
  </div>
}

function Waiting({ place, slot, several, check, next, measuredAt }: { place: number; slot: string; several: boolean; check: MachineCheck; next: boolean; measuredAt: string }) {
  const near = nearLimit(check, measuredAt)
  const wait = waited(check, measuredAt)
  const left = check.wait_limit_ms !== null && check.source === 'queue' ? check.wait_limit_ms - wait : null
  const start = next ? null : startText(check, measuredAt)
  return <li className={near ? 'is-near' : undefined}>
    <span className="checks-place">{place}</span>
    <span className="checks-waiter"><Who check={check} /> <code>{whatText(check)}</code>{several && <span className="checks-faint"> · {slot}</span>}</span>
    <span className="muted">waited {duration(wait)}{left !== null && left > 0 && ` · limit in ${duration(left)}`}</span>
    <span className="checks-next muted">{next ? 'next'
      : start && check.estimate ? <span className="checks-estimate" title={estimateTitle(check.estimate)}>{start}</span> : ''}</span>
  </li>
}

/** The Last hour tab: each reporting machine's slots, what held them and who waited. */
export function LastHourTab({ detail }: { detail: QueueDetail }) {
  const all = machines(detail).filter((machine) => machine.reported_at !== null)
  if (all.length === 0) return <p className="muted">No runner of this project reports its checks yet.</p>
  const now = Date.parse(detail.measured_at)
  const ticks = [60, 45, 30, 15].map((minutes) => clock(now - minutes * 60_000))
  return <div className="checks-tab">
    {all.map((machine) => <HourMachine key={machine.runner} machine={machine} measuredAt={detail.measured_at} ticks={ticks} several={all.length > 1} />)}
    <p className="checks-legend">
      <span><span className="checks-swatch is-queue" />queue (proposal, landing, idle main)</span>
      <span><span className="checks-swatch is-claude" />Claude sessions</span>
      <span><span className="checks-swatch is-codex" />Codex sessions</span>
      <span><span className="checks-swatch is-person" />a person’s terminal</span>
      <span><span className="checks-swatch is-failed" />failed, stopped or timed out</span>
      <span className="checks-faint">Waiting: how many checks waited for a slot.</span>
    </p>
  </div>
}

function HourMachine({ machine, measuredAt, ticks, several }: { machine: QueueMachine; measuredAt: string; ticks: string[]; several: boolean }) {
  const rows = hourRows(machine, measuredAt)
  const series = waitingSeries(machine, measuredAt)
  const most = Math.max(1, ...series)
  const points = series.map((count, index) => `${(index / (series.length - 1) * 100).toFixed(2)},${(12 - count / most * 11).toFixed(2)}`).join(' ')
  const peak = Math.max(...series)
  return <section className="checks-hour" aria-label={`Last hour on ${machine.name}`}>
    {several && <div className="checks-machine-head"><span className="checks-machine-name">{machine.name}</span></div>}
    {rows.map((row) => <div key={row.slot} className="checks-hour-row">
      <span className="checks-slot-name">{row.slot}</span>
      <div className="checks-hour-track" role="list" aria-label={`${row.slot} slot, last hour`}>
        {row.segments.map((segment, index) => <i key={index} role="listitem" className={`is-${segment.tone}`} title={segment.title} aria-label={segment.title}
          style={{ left: `${segment.left}%`, width: `${segment.width}%` }} />)}
      </div>
    </div>)}
    <div className="checks-hour-row">
      <span className="checks-slot-name">Waiting</span>
      <div className="checks-hour-waiting" title={`At most ${peak} waiting`}>
        <svg viewBox="0 0 100 12" preserveAspectRatio="none" role="img" aria-label={`Checks waiting over the last hour, at most ${peak}`}>
          <polygon points={`0,12 ${points} 100,12`} />
          <polyline points={points} />
        </svg>
      </div>
    </div>
    <div className="checks-hour-axis" aria-hidden="true">{ticks.map((tick) => <span key={tick}>{tick}</span>)}<span>now</span></div>
  </section>
}
